import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect, createContext, useContext } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { PlanProvider } from '@/context/PlanContext';
import { Toaster } from '@/components/ui/sonner';
import Layout from '@/components/Layout';
import OnboardingTour from '@/components/OnboardingTour';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import AuthCallback from '@/pages/AuthCallback';
import DashboardPage from '@/pages/DashboardPage';
import TimerPage from '@/pages/TimerPage';
import RegistratiesPage from '@/pages/RegistratiesPage';
import KlantenPage from '@/pages/KlantenPage';
import ProjectenPage from '@/pages/ProjectenPage';
import FacturenPage from '@/pages/FacturenPage';
import CreditNotaPage from '@/pages/CreditNotaPage';
import RapportenPage from '@/pages/RapportenPage';
import InstellingenPage from '@/pages/InstellingenPage';
import AbonnementenPage from '@/pages/AbonnementenPage';
import KilometerCalculatorPage from '@/pages/KilometerCalculatorPage';
import ClientPortalPage from '@/pages/ClientPortalPage';
// Marketing pages
import LandingPage from '@/pages/marketing/LandingPage';
import FeaturesPage from '@/pages/marketing/FeaturesPage';
import PricingPage from '@/pages/marketing/PricingPage';
import AboutPage from '@/pages/marketing/AboutPage';
import ContactPage from '@/pages/marketing/ContactPage';
import BlogPage from '@/pages/marketing/BlogPage';
import './App.css';

// Onboarding Context
const OnboardingContext = createContext(null);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    return { isTourActive: false, startTour: () => {}, completeTour: () => {} };
  }
  return context;
};

const ProtectedRoute = ({ children }) => {
  const { token, loading } = useAuth();
  const { startTour } = useOnboarding();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Laden...</div>
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return <Layout onStartTour={startTour}>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { token, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Laden...</div>
      </div>
    );
  }

  if (token) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions - process BEFORE ProtectedRoute runs
  if (window.location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Client Portal - Public route with token */}
      <Route path="/portal/:token" element={<ClientPortalPage />} />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/timer" element={<ProtectedRoute><TimerPage /></ProtectedRoute>} />
      <Route path="/registraties" element={<ProtectedRoute><RegistratiesPage /></ProtectedRoute>} />
      <Route path="/klanten" element={<ProtectedRoute><KlantenPage /></ProtectedRoute>} />
      <Route path="/projecten" element={<ProtectedRoute><ProjectenPage /></ProtectedRoute>} />
      <Route path="/facturen" element={<ProtectedRoute><FacturenPage /></ProtectedRoute>} />
      <Route path="/creditnotas" element={<ProtectedRoute><CreditNotaPage /></ProtectedRoute>} />
      <Route path="/rapporten" element={<ProtectedRoute><RapportenPage /></ProtectedRoute>} />
      <Route path="/km-calculator" element={<ProtectedRoute><KilometerCalculatorPage /></ProtectedRoute>} />
      <Route path="/instellingen" element={<ProtectedRoute><InstellingenPage /></ProtectedRoute>} />
      <Route path="/abonnementen" element={<ProtectedRoute><AbonnementenPage /></ProtectedRoute>} />
      
      {/* Marketing pages - Public */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/functies" element={<FeaturesPage />} />
      <Route path="/prijzen" element={<PricingPage />} />
      <Route path="/over-ons" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/blog" element={<BlogPage />} />
      
      {/* Catch-all redirect to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function OnboardingWrapper({ children }) {
  const [isTourActive, setIsTourActive] = useState(false);
  const { token, user } = useAuth();

  // Check if tour should be shown for new users
  useEffect(() => {
    if (token && user) {
      const tourCompleted = localStorage.getItem('urenbuddy_tour_completed');
      const isNewRegistration = sessionStorage.getItem('urenbuddy_new_registration');
      
      // Show tour for new registrations or if tour was never completed
      if (isNewRegistration === 'true') {
        sessionStorage.removeItem('urenbuddy_new_registration');
        setIsTourActive(true);
      } else if (!tourCompleted) {
        // Small delay to ensure dashboard is loaded
        const timer = setTimeout(() => {
          setIsTourActive(true);
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [token, user]);

  const startTour = () => {
    setIsTourActive(true);
  };

  const completeTour = () => {
    localStorage.setItem('urenbuddy_tour_completed', 'true');
    setIsTourActive(false);
  };

  return (
    <OnboardingContext.Provider value={{ isTourActive, startTour, completeTour }}>
      {children}
      <OnboardingTour isActive={isTourActive} onComplete={completeTour} />
    </OnboardingContext.Provider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <PlanProvider>
              <OnboardingWrapper>
                <AppRoutes />
              </OnboardingWrapper>
            </PlanProvider>
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
